import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  PauseIcon,
} from "@heroicons/react/24/outline";

// Local imports
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";
import BaseInputText from "../../components/BaseInputText";
import BaseInputSelect from "../../components/BaseInputSelect";
import LoadingClipboardAnimation from "../../components/LoadingClipBoardAnimation";
import StyledMessage from "../../components/StyledMessage";
import { useToastMessageStore } from "../../store/toast-messages";
import {
  getReadableProgramType,
  getReadableTriggerEvent,
  getReadableRewardType,
  LoyaltyProgramType,
  TriggerEvent,
  RewardType,
  ExistingLoyaltyProgramTemplate,
} from "../../models/loyalty-program-template";
import { convertToReadableTimestampDate } from "../../assets/js/convertToReadableTimestamp";
import { DbRead, DbWrite } from "../../database";
import { logger } from "../../logging";
import { createToastMessageID } from "../../utils";
import * as urls from "../../urls";

interface Props {
  siteKey: string;
}

export default function LoyaltyProgramsListContainer({ siteKey }: Props) {
  const navigate = useNavigate();
  const addMessage = useToastMessageStore((state) => state.addToastMessage);

  // Local state
  const [loyaltyPrograms, setLoyaltyPrograms] = useState<
    ExistingLoyaltyProgramTemplate[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isToggling, setIsToggling] = useState<string | null>(null);

  // Fetch data and set up subscriptions
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const setupSubscription = async () => {
      try {
        setIsLoading(true);
        setError(null);

        unsubscribe = DbRead.loyaltyPrograms.subscribe({
          siteKey,
          onChange: (programs: ExistingLoyaltyProgramTemplate[]) => {
            setLoyaltyPrograms(programs);
            setIsLoading(false);
          },
          onError: (err) => {
            logger.error("Error in loyalty programs subscription:", err);
            setError("Failed to load loyalty programs");
            setIsLoading(false);
          },
        });
      } catch (err) {
        logger.error("Error setting up loyalty programs subscription:", err);
        setError("Failed to load loyalty programs");
        setIsLoading(false);
      }
    };

    setupSubscription();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [siteKey]);

  // Filter programs based on search
  const filteredPrograms = loyaltyPrograms.filter(
    (program: ExistingLoyaltyProgramTemplate) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        if (
          !program.name.toLowerCase().includes(searchLower) &&
          !program.description.toLowerCase().includes(searchLower)
        ) {
          return false;
        }
      }

      return true;
    },
  );

  // Navigation handlers
  const handleCreateProgram = () => {
    navigate(urls.LOYALTY_PROGRAMS_CREATE_URL);
  };

  const handleEditProgram = (programId: string) => {
    navigate(`${urls.LOYALTY_PROGRAMS_EDIT_URL}/${programId}`);
  };

  // Action handlers
  const handleToggleStatus = async (
    programId: string,
    currentStatus: boolean,
  ) => {
    setIsToggling(programId);
    try {
      if (currentStatus) {
        // Deactivate by updating the program to set isActive: false
        await DbWrite.loyaltyPrograms.update({
          siteKey,
          programID: programId,
          isActive: false,
        });
        addMessage({
          id: createToastMessageID(),
          message: "Loyalty program deactivated successfully",
          type: "success",
          dialog: false,
        });
      } else {
        // Activate by updating the program to set isActive: true
        await DbWrite.loyaltyPrograms.update({
          siteKey,
          programID: programId,
          isActive: true,
        });
        addMessage({
          id: createToastMessageID(),
          message: "Loyalty program activated successfully",
          type: "success",
          dialog: false,
        });
      }
    } catch (error) {
      logger.error("Error toggling program status:", error);
      addMessage({
        id: createToastMessageID(),
        message: "Failed to update program status",
        type: "error",
        dialog: true,
      });
    } finally {
      setIsToggling(null);
    }
  };

  const handleDeleteProgram = async (programId: string) => {
    if (
      !confirm(
        "Are you sure you want to delete this loyalty program? This action cannot be undone.",
      )
    ) {
      return;
    }

    setIsDeleting(programId);
    try {
      await DbWrite.loyaltyPrograms.delete({
        siteKey,
        programID: programId,
      });
      addMessage({
        id: createToastMessageID(),
        message: "Loyalty program deleted successfully",
        type: "success",
        dialog: false,
      });
    } catch (error) {
      logger.error("Error deleting program:", error);
      addMessage({
        id: createToastMessageID(),
        message: "Failed to delete program",
        type: "error",
        dialog: true,
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  if (isLoading && loyaltyPrograms.length === 0) {
    return (
      <div className="flex h-full flex-auto flex-col items-center justify-center">
        <LoadingClipboardAnimation />
        <p className="mt-4 text-gray-600">Loading loyalty programs...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Loyalty Programs</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your customer loyalty and rewards programs
          </p>
        </div>
        <BaseButtonPrimary onClick={handleCreateProgram}>
          <PlusIcon className="h-4 w-4" />
          Create Program
        </BaseButtonPrimary>
      </div>

      {/* Error Message */}
      {error && (
        <StyledMessage type="error">{{ message: error }}</StyledMessage>
      )}

      {/* Search */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="mb-4 flex items-center gap-4">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">Search</h3>
          {searchTerm && (
            <BaseButtonSecondary onClick={clearSearch} className="text-sm">
              Clear Search
            </BaseButtonSecondary>
          )}
        </div>

        <div className="max-w-md">
          <BaseInputText
            id="search"
            text="Search Programs"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name or description..."
            admin={false}
          />
        </div>
      </div>

      {/* Programs List */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {filteredPrograms.length === 0 ? (
          <div className="py-12 text-center">
            <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {loyaltyPrograms.length === 0
                ? "No loyalty programs"
                : "No programs match your search"}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {loyaltyPrograms.length === 0
                ? "Get started by creating your first loyalty program."
                : "Try adjusting your search terms."}
            </p>
            {loyaltyPrograms.length === 0 && (
              <div className="mt-6">
                <BaseButtonPrimary onClick={handleCreateProgram}>
                  <PlusIcon className="mr-2 h-4 w-4" />
                  Create First Program
                </BaseButtonPrimary>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="border-b border-gray-200 bg-gray-50 px-6 py-3">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-6">
                <div className="lg:col-span-2">
                  <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Program
                  </h3>
                </div>
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Type
                  </h3>
                </div>
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Trigger
                  </h3>
                </div>
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Status
                  </h3>
                </div>
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Actions
                  </h3>
                </div>
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-200">
              {filteredPrograms.map((program) => (
                <div key={program.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-6 lg:items-center">
                    {/* Program Info */}
                    <div className="lg:col-span-2">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {program.name}
                        </h4>
                        <p className="mt-1 line-clamp-2 text-sm text-gray-500">
                          {program.description}
                        </p>
                        <div className="mt-1 flex items-center text-xs text-gray-400">
                          <span>
                            Created:{" "}
                            {convertToReadableTimestampDate(
                              program.timestampCreated,
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Type */}
                    <div>
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">
                          {getReadableProgramType(program.programType)}
                        </div>
                        <div className="text-gray-500">
                          {getReadableRewardType(program.rewardType)}
                        </div>
                      </div>
                    </div>

                    {/* Trigger */}
                    <div>
                      <span className="text-sm text-gray-900">
                        {getReadableTriggerEvent(program.triggerEvent)}
                      </span>
                    </div>

                    {/* Status */}
                    <div>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          program.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {program.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>

                    {/* Actions */}
                    <div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditProgram(program.id)}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Edit Program"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() =>
                            handleToggleStatus(program.id, program.isActive)
                          }
                          disabled={isToggling === program.id}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                          title={
                            program.isActive
                              ? "Deactivate Program"
                              : "Activate Program"
                          }
                        >
                          {isToggling === program.id ? (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                          ) : program.isActive ? (
                            <PauseIcon className="h-4 w-4" />
                          ) : (
                            <PlayIcon className="h-4 w-4" />
                          )}
                        </button>

                        <button
                          onClick={() => handleDeleteProgram(program.id)}
                          disabled={isDeleting === program.id}
                          className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
                          title="Delete Program"
                        >
                          {isDeleting === program.id ? (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-red-600" />
                          ) : (
                            <TrashIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Results Summary */}
      {filteredPrograms.length > 0 && (
        <div className="text-center text-sm text-gray-500">
          Showing {filteredPrograms.length} of {loyaltyPrograms.length} programs
        </div>
      )}
    </div>
  );
}
